import { useId, useLayoutEffect, useRef, useState, type RefObject } from 'react'
import { createPortal } from 'react-dom'
import { Icon } from '../ui/Icon'
import type { IconName } from '../ui/icons.generated'

/**
 * ONE BUTTON FOR WHAT A ROW CAN DO — owner, 2026-11-04: 把 PIN Agent 跟按下 Agent 的頭部的那個設定按鈕合併起來.
 * 這兩個按鈕可以用一個垂直 ⋮ 的形狀的 icon 來代表, 使用者在點下這三個點之後, 可以把 agent pin 到看板上面, 也可以做
 * agent 的設定.
 *
 * The row had two ways in: the pin at its far end (which opens the tag sheet) and the face, which opened the agent's
 * card and wore a gear to say so. Both are here now, as the two items of one menu; the face is only a face again, and
 * a press on it opens the session like the rest of the row.
 *
 * THE MENU LIVES ON <body>. The rail scrolls and clips, and a row near its bottom would cut its own menu in half. So it
 * is portalled and placed against the button: under it, its right edge on the button's, or above it when there is no
 * room below. When the rail scrolls or the window resizes it moves with the button, and it closes once the button is
 * out of sight.
 *
 * React still bubbles a portal's events to the row it was opened from, and the row opens the session on a click and on
 * Enter. Everything that happens inside the menu stops at the menu.
 *
 * A press anywhere else lands on a clear shield and only closes it. The shield answers the CLICK, not the press: a tap
 * is a press and a click, and a shield gone by the click would hand it to the row underneath, which opens that agent.
 */
export type RowMenuItem = { key: string; icon: IconName; label: string; on?: boolean; act: () => void }

export function AgentRowMenu({ session, items }: { session: string; items: RowMenuItem[] }) {
  const [open, setOpen] = useState(false)
  const btn = useRef<HTMLButtonElement>(null)
  const menuId = useId()
  return (
    <>
      <button
        ref={btn}
        type="button"
        className={`sb-more${open ? ' open' : ''}`}
        title={`Pin or settings — ${session}`}
        aria-label={`More for ${session}`}
        aria-haspopup="menu"
        aria-expanded={open}
        {...(open ? { 'aria-controls': menuId } : {})}
        onClick={(e) => {
          e.stopPropagation()
          setOpen(!open)
        }}
        /* The row answers Enter and Space by opening the session; the button keeps them, as the pin did. */
        onKeyDown={(e) => {
          e.stopPropagation()
          if (e.key === 'ArrowDown' && !open) {
            e.preventDefault()
            setOpen(true)
          }
        }}
      >
        <Icon name="more_vert" size={16} />
      </button>
      {open && (
        <RowMenu
          id={menuId}
          anchor={btn}
          label={`Actions for ${session}`}
          items={items}
          onClose={(refocus) => {
            setOpen(false)
            if (refocus) btn.current?.focus()
          }}
        />
      )}
    </>
  )
}

function RowMenu({
  id,
  anchor,
  label,
  items,
  onClose,
}: {
  id: string
  anchor: RefObject<HTMLButtonElement | null>
  label: string
  items: RowMenuItem[]
  onClose: (refocus: boolean) => void
}) {
  const ref = useRef<HTMLDivElement>(null)
  const close = useRef(onClose)
  close.current = onClose

  useLayoutEffect(() => {
    const m = ref.current
    const el = anchor.current
    if (!m || !el) return
    /* The rail's list, which scrolls the button and clips it: the menu goes when the button leaves it. */
    let clipper: HTMLElement | null = el.parentElement
    while (clipper && !/(auto|scroll)/.test(getComputedStyle(clipper).overflowY)) clipper = clipper.parentElement
    /** Put the menu against the button. False once the button is out of sight — scrolled off, or gone. */
    const place = () => {
      if (!el.isConnected) return false
      const b = el.getBoundingClientRect()
      const c = clipper?.getBoundingClientRect()
      const bottom = Math.min(window.innerHeight, c?.bottom ?? Infinity)
      const top0 = Math.max(0, c?.top ?? -Infinity)
      if (b.bottom < top0 || b.top > bottom) return false
      const w = m.offsetWidth
      const h = m.offsetHeight
      const left = Math.max(8, Math.min(b.right - w, window.innerWidth - w - 8))
      const below = b.bottom + 4
      const top = below + h <= window.innerHeight - 8 ? below : Math.max(8, b.top - 4 - h)
      m.style.left = `${left}px`
      m.style.top = `${top}px`
      return true
    }
    if (!place()) {
      close.current(false)
      return
    }
    m.style.visibility = 'visible'
    m.querySelector<HTMLElement>('[role="menuitem"]')?.focus({ preventScroll: true })
    /*
     * It FOLLOWS the button rather than closing on the first scroll. Closing on any scroll closed it the moment it
     * opened whenever anything scrolled — the rail settling after the press, or a conversation streaming beside it — so
     * only a scroll that carries the button counts, and that one moves the menu with it.
     */
    const follow = () => {
      if (!place()) close.current(false)
    }
    const onScroll = (e: Event) => {
      if (e.target instanceof Node && e.target.contains(el)) follow()
    }
    window.addEventListener('resize', follow)
    window.addEventListener('scroll', onScroll, true)
    return () => {
      window.removeEventListener('resize', follow)
      window.removeEventListener('scroll', onScroll, true)
    }
  }, [anchor])

  const move = (from: HTMLElement, by: number | 'first' | 'last') => {
    const all = [...(ref.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [])]
    const i = all.indexOf(from)
    const next = by === 'first' ? 0 : by === 'last' ? all.length - 1 : (i + by + all.length) % all.length
    all[next]?.focus()
  }

  return createPortal(
    <div
      className="sb-more-layer"
      onClick={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
      onContextMenu={(e) => e.stopPropagation()}
    >
      <div
        className="sb-more-shield"
        onClick={() => onClose(false)}
        onContextMenu={(e) => {
          e.preventDefault()
          onClose(false)
        }}
      />
      <div
        ref={ref}
        id={id}
        className="sb-more-menu"
        role="menu"
        aria-label={label}
        style={{ left: 0, top: 0, visibility: 'hidden' }}
        onKeyDown={(e) => {
          const at = e.target as HTMLElement
          if (e.key === 'Escape') {
            e.preventDefault()
            onClose(true)
          } else if (e.key === 'Tab') {
            e.preventDefault()
            onClose(true)
          } else if (e.key === 'ArrowDown') {
            e.preventDefault()
            move(at, 1)
          } else if (e.key === 'ArrowUp') {
            e.preventDefault()
            move(at, -1)
          } else if (e.key === 'Home') {
            e.preventDefault()
            move(at, 'first')
          } else if (e.key === 'End') {
            e.preventDefault()
            move(at, 'last')
          }
        }}
      >
        {items.map((it) => (
          <button
            key={it.key}
            type="button"
            role="menuitem"
            className={`sb-more-item${it.on ? ' on' : ''}`}
            onClick={() => {
              onClose(false)
              it.act()
            }}
          >
            <Icon name={it.icon} size={16} />
            <span>{it.label}</span>
          </button>
        ))}
      </div>
    </div>,
    document.body,
  )
}
